import React, { useEffect, useReducer, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Stack,
  TextField
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRecoilState } from "recoil";
import { parksState } from "../../../atoms/parks";
import APIService from "../../../services/api.service";
import { tokenState } from "../../../atoms/token";
import { CacheService } from "../../../common/services/cache.service";
import environment from "../../../environment/environment";
import dayjs, { Dayjs } from "dayjs";
import "./SearchFilter.scss";
import axios from "axios";

const SearchFilter = ({
  clearFilters = () => {},
  handleFilterChange,
  filterText = null,
  filters = null,
  expanded = false
}) => {
  const [parks] = useRecoilState(parksState);
  const [token] = useRecoilState(tokenState);
  const [infoTypes, setInfoTypes] = useState<string[]>([]);
  const [securityTypes, setSecurityTypes] = useState<any[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(expanded);

  const [form, setForm] = useReducer(
    (form, updates) => ({ ...form, ...updates }),
    {}
  );

  // UI control values
  const [localText, setLocalText] = useState<string>();
  const [parkAlphaList, setParkAlphaList] = useState<any[]>([]);
  const [infoTypeList, setInfoTypeList] = useState<any[]>([]);
  const [securityTypeList, setSecurityTypeList] = useState<any[]>([]);
  const [locationList, setLocationList] = useState<any[]>([]);
  const [fromYear, setFromYear] = useState<Dayjs | null>(null);
  const [toYear, setToYear] = useState<Dayjs | null>(null);

  useEffect(() => {
    const initialForm = {
      filterText: filterText || '',
      parkAlphaList: filters?.parkAlphaList || [],
      infoTypeList: filters?.infoTypeList || [],
      securityTypeList: filters?.securityTypeList || [],
      locationList: filters?.locationList || [],
      ticFromYear: filters?.ticFromYear ?? null,
      ticToYear: filters?.ticToYear ?? null,
    };

    if (filterText !== localText) {
      setLocalText(filterText);
    }
    if (filters) {    
      setParkAlphaList(initialForm.parkAlphaList);
      setInfoTypeList(initialForm.infoTypeList);
      setSecurityTypeList(initialForm.securityTypeList);
      setLocationList(initialForm.locationList);
      setFromYear(initialForm.ticFromYear ? dayjs(initialForm.ticFromYear.toString()) : null);
      setToYear(initialForm.ticToYear ? dayjs(initialForm.ticToYear.toString()) : null);
    }

    // MANUALLY compute the correct new form
    const newForm = { ...form, ...initialForm };
    setForm(newForm);
    refreshDropDowns(newForm);
  }, [filters, filterText]);

  const handleChange = (fieldName: string, value: any) => {
    let updates: any = {};

    switch (fieldName) {
      case 'parkAlphaList':
        setParkAlphaList(value);
        updates.parkAlphaList = value;
        break;
      case 'infoTypeList':
        setInfoTypeList(value);
        updates.infoTypeList = value;
        break;
      case 'securityTypeList':
        setSecurityTypeList(value);
        updates.securityTypeList = value;
        break;
      case 'locationList':
        setLocationList(value);
        updates.locationList = value;
        break;
      case 'ticFromYear':
        setFromYear(value);
        updates.ticFromYear = value?.$y ?? null;
        break;
      case 'ticToYear':
        setToYear(value);
        updates.ticToYear = value?.$y ?? null;
        break;
    }

    // MANUALLY compute the correct new form
    const newForm = { ...form, ...updates };

    setForm(newForm);           // Update local form state
    handleFilterChange(newForm);// Tell parent
    refreshDropDowns(newForm);  // Fetch updated dropdown options based on latest form
  };

  const refreshDropDowns = (currentForm: any) => {
    ['ticInfoType', 'ticDocType', 'ticLocation'].forEach((field) => {
      getDropDownData(field, currentForm);
    });
  };

  const getDropDownData = (filterField: string, formState: any) => {
    let queryParts = [`filterfield=${encodeURIComponent(filterField)}`];    

    if (formState.filterText) {
      queryParts.push(`allfields=${encodeURIComponent(formState.filterText)}`);
    }

    (formState.parkAlphaList ?? []).forEach((park: any) => queryParts.push(`ticParkAlpha=${encodeURIComponent(park.alpha)}`));
    (formState.infoTypeList ?? []).forEach((info: string) => queryParts.push(`ticInfoType=${encodeURIComponent(info)}`));
    (formState.securityTypeList ?? []).forEach((sec: any) => queryParts.push(`ticDocType=${encodeURIComponent(sec[1])}`));
    (formState.locationList ?? []).forEach((loc: string) => queryParts.push(`ticLocation=${encodeURIComponent(loc)}`));

    const url = `${environment.eticApiUrl}search?${queryParts.join('&')}`;
    const apiService = new APIService<any>(axios, new CacheService, url.toString(), token);
    apiService.getAnyAsync().then((res: any) => {
      if (filterField === 'ticInfoType') setInfoTypes(res);
      if (filterField === 'ticDocType') setSecurityTypes(res);
      if (filterField === 'ticLocation') setLocations(res);
    });
  };

  const handleClearFilters = () => {
    setParkAlphaList([]);
    setInfoTypeList([]);
    setSecurityTypeList([]);
    setLocationList([]);
    setFromYear(null);
    setToYear(null);
    setForm({});
    clearFilters();
  };

  return (
    <div>
      <Accordion defaultExpanded={isExpanded}>
        <AccordionSummary
          className="center"
          expandIcon={<FontAwesomeIcon icon={["fas", "chevron-down"]} />}
        >
          <FontAwesomeIcon icon={["fas", "search"]} />
          <span className="margin">Search Filters</span>
        </AccordionSummary>

        <AccordionDetails>
          <Stack spacing={1} direction="row" sx={{ mb: 1 }}>
            <Button
              id="secondary"
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={["fas", "circle-x"]} />}
              onClick={handleClearFilters}
            >
              Reset Search
            </Button>
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={parks}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.alpha === value?.alpha}
              value={parkAlphaList}
              sx={{ width: 500 }}
              size="small"
              onChange={(e, newValue) => handleChange('parkAlphaList', newValue)}
              renderInput={(params) => <TextField {...params} variant="standard" label="Park Name" />}
            />

            <Autocomplete
              multiple
              options={infoTypes}
              getOptionLabel={(option) => option}
              value={infoTypeList}
              sx={{ width: 500 }}
              size="small"
              onChange={(e, newValue) => handleChange('infoTypeList', newValue)}
              renderInput={(params) => <TextField {...params} variant="standard" label="Info Type" />}
            />

            <Autocomplete
              multiple
              options={securityTypes}
              getOptionLabel={(option) => option?.[1] || ""}
              isOptionEqualToValue={(option, value) => option?.[1] === value?.[1]}
              value={securityTypeList}
              sx={{ width: 500 }}
              size="small"
              onChange={(e, newValue) => handleChange('securityTypeList', newValue)}
              renderInput={(params) => <TextField {...params} variant="standard" label="Security Type" />}
            />

            <Autocomplete
              multiple
              options={locations}
              getOptionLabel={(option) => option}
              value={locationList}
              sx={{ width: 500 }}
              size="small"
              onChange={(e, newValue) => handleChange('locationList', newValue)}
              renderInput={(params) => <TextField {...params} variant="standard" label="Location" />}
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                slotProps={{ textField: { size: 'small' } }}
                value={fromYear}
                onChange={(date) => handleChange('ticFromYear', date)}
                views={["year"]}
                label="From Year"
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                slotProps={{ textField: { size: 'small' } }}
                value={toYear}
                onChange={(date) => handleChange('ticToYear', date)}
                views={["year"]}
                label="To Year"
              />
            </LocalizationProvider>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default SearchFilter;
